import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { 
  Shield, AlertTriangle, Users, FileText, Clock, CheckCircle, XCircle, 
  Trash2, Search, Plus, Building, Building2, BarChart3, Megaphone, Settings, 
  Ban, UserX, UserCheck, Eye, Edit, Power, ChevronRight, TrendingUp,
  MapPin, Mail, Phone, Globe, RefreshCw, AlertOctagon, Image as ImageIcon,
  Ticket, UserPlus, ArrowRight, MessageSquare, HelpCircle, Bug, Lightbulb,
  HandHeart, Play, Pause, Loader2, Store
} from "lucide-react";
import { 
  getAdminStats, getReportedPosts, takeReportAction, getReportHistory,
  getAdminUsers, getUserWarnings, getUserDetails, banUser, unbanUser, 
  getBannedUsers, deleteUser, forcePasswordReset, promoteSiteAdmin, demoteSiteAdmin,
  getAdminParishes, createAdminParish, getAdminParishDetails, updateAdminParish, deleteAdminParish,
  suspendParish, reactivateParish, adminAssignParishAdmin, adminRemoveParishAdmin,
  getAnalyticsOverview, getUserGrowth, getParishRankings, getContentStats,
  getSiteAnnouncements, createSiteAnnouncement, deleteSiteAnnouncement, toggleAnnouncement,
  getAdminPosts, deletePostAdmin, getAdminListings, deleteListingAdmin, 
  getAdminGroups, deleteGroupAdmin, getSystemSettings, updateSystemSettings,
  getAdminInvites, getInviteStats,
  getAdminSupportTickets, getSupportTicketStats, updateSupportTicket, deleteSupportTicket,
  assignUserToParish, getParishesListForAssignment,
  getAdminBusinesses, suspendBusinessAdmin, reactivateBusinessAdmin, deleteBusinessAdmin,
  getAdminRequests, suspendRequestAdmin, reactivateRequestAdmin, deleteRequestAdmin,
  getAdminJoinRequests, adminApproveJoinRequest, adminRejectJoinRequest,
  resolveImageUrl, adminCreateListing, adminUpdateListing, adminAssignOwner, searchUsers,
  getBusinessCategories
} from "@/lib/api";
import { toast } from "sonner";
import { ParishSearchPicker } from "@/components/ParishSearchPicker";

// =============================================================================
// STAT CARD COMPONENT
// =============================================================================
function StatCard({ title, value, icon: Icon, color, subtitle, onClick }) {
  return (
    <Card 
      className={`bg-white/80 border-[#E8E4DC] ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-['Nunito'] text-[#6B665E]">{title}</p>
            <p className="text-2xl font-bold font-['Cinzel'] text-[#2C2C2C] mt-1">{value}</p>
            {subtitle && <p className="text-xs text-[#6B665E] mt-1">{subtitle}</p>}
          </div>
          <div className={`p-2 rounded-lg ${color}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// REPORTS TAB COMPONENT
// =============================================================================
function ReportsTab({ onActionComplete }) {
  const [reports, setReports] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState("pending");
  const [actionDialog, setActionDialog] = useState({ open: false, report: null });
  const [selectedAction, setSelectedAction] = useState("");
  const [actionNote, setActionNote] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const [reportsRes, historyRes] = await Promise.all([
        getReportedPosts("pending"),
        getReportHistory()
      ]);
      setReports(reportsRes.data.reports);
      setHistory(historyRes.data.reports);
    } catch {
      toast.error("Failed to load reports");
    }
    setLoading(false);
  };

  const handleAction = async () => {
    if (!selectedAction) {
      toast.error("Please select an action");
      return;
    }
    setProcessing(true);
    try {
      await takeReportAction(actionDialog.report.report_id, selectedAction, actionNote);
      toast.success("Action taken successfully");
      setActionDialog({ open: false, report: null });
      setSelectedAction("");
      setActionNote("");
      loadReports();
      onActionComplete?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to process action");
    }
    setProcessing(false);
  };

  if (loading) {
    return <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-[var(--liturgical-primary)]" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2 mb-4">
        <Button
          variant={activeSubTab === "pending" ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveSubTab("pending")}
          className={activeSubTab === "pending" ? "bg-[var(--liturgical-primary)]" : ""}
        >
          Pending ({reports.length})
        </Button>
        <Button
          variant={activeSubTab === "history" ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveSubTab("history")}
          className={activeSubTab === "history" ? "bg-[var(--liturgical-primary)]" : ""}
        >
          History ({history.length})
        </Button>
      </div>

      {activeSubTab === "pending" && (
        reports.length === 0 ? (
          <Card className="bg-white border-[#E8E4DC]">
            <CardContent className="py-12 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-['Cinzel'] font-semibold text-[#2C2C2C] mb-2">All Clear!</h3>
              <p className="text-[#6B665E] font-['Nunito']">No pending reports to review.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {reports.map((report) => (
              <Card key={report.report_id} className="bg-white border-[#E8E4DC]">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-yellow-100 text-yellow-800">{report.reason}</Badge>
                      {report.total_reports > 1 && (
                        <Badge className="bg-red-500 text-white">{report.total_reports} reports</Badge>
                      )}
                    </div>
                    <span className="text-xs text-[#6B665E]">
                      {new Date(report.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  
                  {report.post && (
                    <div className="bg-[#F9F7F2] rounded-lg p-3 mb-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Avatar className="w-6 h-6">
                          <AvatarImage src={resolveImageUrl(report.post.author?.picture)} />
                          <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-[10px]">
                            {report.post.author?.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-['Nunito'] font-semibold text-sm">{report.post.author?.name}</span>
                      </div>
                      <p className="text-sm text-[#2C2C2C] line-clamp-2">{report.post.content}</p>
                    </div>
                  )}
                  
                  <div className="flex justify-end">
                    <Button size="sm" onClick={() => setActionDialog({ open: true, report })}>
                      Take Action
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      )}

      {activeSubTab === "history" && (
        <div className="space-y-2">
          {history.map((report, idx) => (
            <Card key={idx} className="bg-white/60 border-[#E8E4DC]">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <Badge className={
                    report.status === "dismissed" ? "bg-green-100 text-green-800" :
                    report.status === "post_removed" ? "bg-red-100 text-red-800" :
                    "bg-yellow-100 text-yellow-800"
                  }>
                    {report.status?.replace("_", " ")}
                  </Badge>
                  <span className="text-xs text-[#6B665E]">
                    {report.resolved_at && new Date(report.resolved_at).toLocaleDateString()}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => setActionDialog({ open, report: actionDialog.report })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Cinzel']">Take Action on Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Action</Label>
              <Select value={selectedAction} onValueChange={setSelectedAction}>
                <SelectTrigger><SelectValue placeholder="Select action..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="approve">Dismiss Report (Post is OK)</SelectItem>
                  <SelectItem value="warn">Warn Author (Keep Post)</SelectItem>
                  <SelectItem value="remove">Remove Post</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Note (optional)</Label>
              <Textarea
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
                placeholder="Add a note..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, report: null })}>Cancel</Button>
            <Button onClick={handleAction} disabled={processing} className="bg-[var(--liturgical-primary)]">
              {processing ? "Processing..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =============================================================================
// USERS TAB COMPONENT
// =============================================================================
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [bannedUsers, setBannedUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState("all");
  const [userDialog, setUserDialog] = useState({ open: false, user: null, details: null });
  const [banDialog, setBanDialog] = useState({ open: false, user: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [assignParishDialog, setAssignParishDialog] = useState({ open: false, user: null });
  const [parishes, setParishes] = useState([]);
  const [selectedParish, setSelectedParish] = useState("");
  const [makeParishAdmin, setMakeParishAdmin] = useState(false);
  const [banReason, setBanReason] = useState("");
  const [banDuration, setBanDuration] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadUsers();
    loadBannedUsers();
    loadParishes();
  }, []);

  const loadParishes = async () => {
    try {
      const res = await getParishesListForAssignment();
      setParishes(res.data.parishes || []);
    } catch {}
  };

  const loadUsers = async (searchQuery = "") => {
    setLoading(true);
    try {
      const res = await getAdminUsers({ search: searchQuery, limit: 100 });
      setUsers(res.data.users);
    } catch {
      toast.error("Failed to load users");
    }
    setLoading(false);
  };

  const loadBannedUsers = async () => {
    try {
      const res = await getBannedUsers();
      setBannedUsers(res.data.bans);
    } catch {}
  };

  const handleSearch = (value) => {
    setSearch(value);
    const timeout = setTimeout(() => loadUsers(value), 300);
    return () => clearTimeout(timeout);
  };

  const viewUserDetails = async (user) => {
    try {
      const res = await getUserDetails(user.user_id);
      setUserDialog({ open: true, user, details: res.data });
    } catch {
      toast.error("Failed to load user details");
    }
  };

  const handleBan = async () => {
    if (!banReason.trim()) {
      toast.error("Please provide a reason");
      return;
    }
    setProcessing(true);
    try {
      const duration = banDuration ? parseInt(banDuration) : null;
      await banUser(banDialog.user.user_id, banReason, duration);
      toast.success("User suspended successfully");
      setBanDialog({ open: false, user: null });
      setBanReason("");
      setBanDuration("");
      loadUsers(search);
      loadBannedUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to suspend user");
    }
    setProcessing(false);
  };

  const handleUnban = async (userId) => {
    try {
      await unbanUser(userId);
      toast.success("User reinstated");
      loadUsers(search);
      loadBannedUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reinstate user");
    }
  };

  const handleDelete = async () => {
    if (!deleteReason.trim()) {
      toast.error("Please provide a reason for deletion");
      return;
    }
    setProcessing(true);
    try {
      await deleteUser(deleteDialog.user.user_id, deleteReason);
      toast.success("User deleted successfully");
      setDeleteDialog({ open: false, user: null });
      setDeleteReason("");
      loadUsers(search);
      setUserDialog({ open: false, user: null, details: null });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    }
    setProcessing(false);
  };

  const openDeleteDialog = (user) => {
    setDeleteDialog({ open: true, user });
    setDeleteReason("");
  };

  const handlePromote = async (userId) => {
    try {
      await promoteSiteAdmin(userId);
      toast.success("User promoted to site admin");
      loadUsers(search);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to promote user");
    }
  };

  const handleDemote = async (userId) => {
    try {
      await demoteSiteAdmin(userId);
      toast.success("User demoted from site admin");
      loadUsers(search);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to demote user");
    }
  };

  const openAssignParishDialog = (user) => {
    setAssignParishDialog({ open: true, user });
    setSelectedParish(user.parish_id || "");
    setMakeParishAdmin(false);
  };

  const handleAssignParish = async () => {
    if (!selectedParish) {
      toast.error("Please select a parish");
      return;
    }
    setProcessing(true);
    try {
      await assignUserToParish(assignParishDialog.user.user_id, selectedParish, makeParishAdmin);
      toast.success("User assigned to parish successfully");
      setAssignParishDialog({ open: false, user: null });
      setSelectedParish("");
      setMakeParishAdmin(false);
      loadUsers(search);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign user to parish");
    }
    setProcessing(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 mb-4">
        <Button
          variant={activeSubTab === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveSubTab("all")}
          className={activeSubTab === "all" ? "bg-[var(--liturgical-primary)]" : ""}
        >
          <Users className="w-4 h-4 mr-1" /> All Users
        </Button>
        <Button
          variant={activeSubTab === "banned" ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveSubTab("banned")}
          className={activeSubTab === "banned" ? "bg-[var(--liturgical-primary)]" : ""}
        >
          <Ban className="w-4 h-4 mr-1" /> Suspended ({bannedUsers.length})
        </Button>
      </div>

      {activeSubTab === "all" && (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
            <Input
              placeholder="Search users..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10 bg-white"
            />
          </div>

          {loading ? (
            <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-[var(--liturgical-primary)]" /></div>
          ) : (
            <div className="space-y-2">
              {users.map((user) => (
                <Card key={user.user_id} className="bg-white border-[#E8E4DC]">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={resolveImageUrl(user.picture)} />
                          <AvatarFallback className="bg-[var(--liturgical-primary)] text-white">{user.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-['Nunito'] font-semibold">{user.name}</p>
                          <p className="text-xs text-[#6B665E]">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {user.is_site_admin && <Badge className="bg-purple-100 text-purple-800">Site Admin</Badge>}
                        {user.is_banned && <Badge className="bg-red-100 text-red-800">Banned</Badge>}
                        <Button variant="ghost" size="sm" onClick={() => viewUserDetails(user)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {!user.is_banned ? (
                          <Button variant="ghost" size="sm" onClick={() => setBanDialog({ open: true, user })}>
                            <Ban className="w-4 h-4 text-red-500" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => handleUnban(user.user_id)}>
                            <UserCheck className="w-4 h-4 text-green-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {activeSubTab === "banned" && (
        <div className="space-y-2">
          {bannedUsers.length === 0 ? (
            <Card className="bg-white border-[#E8E4DC]">
              <CardContent className="py-8 text-center">
                <UserCheck className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <p className="text-[#6B665E]">No banned users</p>
              </CardContent>
            </Card>
          ) : (
            bannedUsers.map((ban) => (
              <Card key={ban.ban_id} className="bg-white border-[#E8E4DC]">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={resolveImageUrl(ban.user?.picture)} />
                        <AvatarFallback className="bg-red-500 text-white">{ban.user?.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-['Nunito'] font-semibold">{ban.user?.name}</p>
                        <p className="text-xs text-red-600">{ban.reason}</p>
                        <p className="text-xs text-[#6B665E]">
                          {ban.duration_days ? `${ban.duration_days} days` : "Permanent"}
                        </p>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => handleUnban(ban.user_id)}>
                      Reinstate
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* User Details Dialog */}
      <Dialog open={userDialog.open} onOpenChange={(open) => setUserDialog({ ...userDialog, open })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Cinzel']">User Details</DialogTitle>
          </DialogHeader>
          {userDialog.details && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={resolveImageUrl(userDialog.details.user.picture)} />
                  <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-xl">
                    {userDialog.details.user.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-['Cinzel'] font-bold text-lg">{userDialog.details.user.name}</h3>
                  <p className="text-sm text-[#6B665E]">{userDialog.details.user.email}</p>
                  {userDialog.details.parish && (
                    <p className="text-sm text-[#6B665E]">{userDialog.details.parish.name}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-[#F9F7F2] rounded p-2">
                  <p className="text-lg font-bold">{userDialog.details.stats.posts}</p>
                  <p className="text-xs text-[#6B665E]">Posts</p>
                </div>
                <div className="bg-[#F9F7F2] rounded p-2">
                  <p className="text-lg font-bold">{userDialog.details.stats.comments}</p>
                  <p className="text-xs text-[#6B665E]">Comments</p>
                </div>
                <div className="bg-[#F9F7F2] rounded p-2">
                  <p className="text-lg font-bold">{userDialog.details.stats.warnings}</p>
                  <p className="text-xs text-[#6B665E]">Warnings</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => openAssignParishDialog(userDialog.details.user)}>
                  <Building className="w-4 h-4 mr-1" /> Assign to Parish
                </Button>
                {!userDialog.details.user.is_site_admin ? (
                  <Button size="sm" variant="outline" onClick={() => handlePromote(userDialog.details.user.user_id)}>
                    <Shield className="w-4 h-4 mr-1" /> Promote to Admin
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => handleDemote(userDialog.details.user.user_id)}>
                    <UserX className="w-4 h-4 mr-1" /> Demote from Admin
                  </Button>
                )}
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => openDeleteDialog(userDialog.details.user)}>
                  <Trash2 className="w-4 h-4 mr-1" /> Delete User
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Ban Dialog */}
      <Dialog open={banDialog.open} onOpenChange={(open) => setBanDialog({ ...banDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Cinzel']">Suspend User</DialogTitle>
            <DialogDescription>Suspend {banDialog.user?.name} from the platform.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason for suspension *</Label>
              <Textarea
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="Reason for suspension..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Duration (days, leave empty for permanent)</Label>
              <Input
                type="number"
                value={banDuration}
                onChange={(e) => setBanDuration(e.target.value)}
                placeholder="e.g., 7"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBanDialog({ open: false, user: null })}>Cancel</Button>
            <Button onClick={handleBan} disabled={processing} className="bg-red-600 hover:bg-red-700">
              {processing ? "Banning..." : "Suspend User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Cinzel'] text-red-600">Delete User Permanently</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete <strong>{deleteDialog.user?.name}</strong>'s account and all associated data including posts, messages, and listings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason for deletion *</Label>
              <Textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="e.g., Repeated violations of community guidelines..."
                rows={3}
              />
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700 font-['Nunito']">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                The user will receive an email notification explaining the deletion.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, user: null })}>Cancel</Button>
            <Button onClick={handleDelete} disabled={processing} className="bg-red-600 hover:bg-red-700">
              {processing ? "Deleting..." : "Delete Permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign to Parish Dialog */}
      <Dialog open={assignParishDialog.open} onOpenChange={(open) => setAssignParishDialog({ ...assignParishDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Cinzel']">Assign User to Parish</DialogTitle>
            <DialogDescription>
              Assign <strong>{assignParishDialog.user?.name}</strong> to a parish.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <ParishSearchPicker
                value={selectedParish}
                onChange={(parishId) => setSelectedParish(parishId)}
                label="Select Parish *"
                placeholder="Search by name, city, or zip..."
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="make-admin"
                checked={makeParishAdmin}
                onCheckedChange={setMakeParishAdmin}
              />
              <Label htmlFor="make-admin">Make this user a Parish Admin</Label>
            </div>
            {assignParishDialog.user?.parish_name && (
              <div className="bg-[#F9F7F2] rounded-lg p-3">
                <p className="text-sm text-[#6B665E] font-['Nunito']">
                  <Building className="w-4 h-4 inline mr-1" />
                  Currently in: <strong>{assignParishDialog.user.parish_name}</strong>
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignParishDialog({ open: false, user: null })}>Cancel</Button>
            <Button onClick={handleAssignParish} disabled={processing} className="bg-[var(--liturgical-primary)]">
              {processing ? "Assigning..." : "Assign to Parish"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =============================================================================
// PARISHES TAB COMPONENT
// =============================================================================
function ParishesTab() {
  const [parishes, setParishes] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [detailDialog, setDetailDialog] = useState({ open: false, parish: null, details: null });
  const [assignAdminDialog, setAssignAdminDialog] = useState({ open: false, parish: null });
  const [newParish, setNewParish] = useState({ name: "", city: "", state: "", country: "", diocese: "" });
  const [processing, setProcessing] = useState(false);
  
  // For assigning admin - user search
  const [userSearch, setUserSearch] = useState("");
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);

  useEffect(() => {
    loadParishes();
  }, []);

  const loadParishes = async (searchQuery = "") => {
    setLoading(true);
    try {
      const res = await getAdminParishes({ search: searchQuery, limit: 100 });
      setParishes(res.data.parishes);
    } catch {
      toast.error("Failed to load parishes");
    }
    setLoading(false);
  };

  const handleSearch = (value) => {
    setSearch(value);
    setTimeout(() => loadParishes(value), 300);
  };

  const handleCreate = async () => {
    if (!newParish.name.trim()) {
      toast.error("Parish name is required");
      return;
    }
    setProcessing(true);
    try {
      await createAdminParish(newParish);
      toast.success("Parish created successfully");
      setCreateDialog(false);
      setNewParish({ name: "", city: "", state: "", country: "", diocese: "" });
      loadParishes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create parish");
    }
    setProcessing(false);
  };

  const viewParishDetails = async (parish) => {
    try {
      const res = await getAdminParishDetails(parish.parish_id);
      setDetailDialog({ open: true, parish, details: res.data });
    } catch {
      toast.error("Failed to load parish details");
    }
  };

  const handleSuspend = async (parishId) => {
    try {
      await suspendParish(parishId);
      toast.success("Parish suspended");
      loadParishes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to suspend parish");
    }
  };

  const handleReactivate = async (parishId) => {
    try {
      await reactivateParish(parishId);
      toast.success("Parish reactivated");
      loadParishes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reactivate parish");
    }
  };

  const handleDelete = async (parishId) => {
    if (!confirm("Are you sure? This will delete the parish and disassociate all members.")) return;
    try {
      await deleteAdminParish(parishId);
      toast.success("Parish deleted");
      loadParishes();
      setDetailDialog({ open: false, parish: null, details: null });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete parish");
    }
  };

  // Search users for assigning as admin
  const searchUsersForAdmin = async (query) => {
    if (!query.trim()) {
      setUserSearchResults([]);
      return;
    }
    setSearchingUsers(true);
    try {
      const res = await getAdminUsers({ search: query, limit: 10 });
      // Filter out users who are already admins of this parish
      const currentAdminIds = detailDialog.details?.admins?.map(a => a.user_id) || [];
      const filtered = res.data.users.filter(u => !currentAdminIds.includes(u.user_id));
      setUserSearchResults(filtered);
    } catch {
      toast.error("Failed to search users");
    }
    setSearchingUsers(false);
  };

  const handleAssignAdmin = async (userId) => {
    if (!assignAdminDialog.parish) return;
    setProcessing(true);
    try {
      await adminAssignParishAdmin(assignAdminDialog.parish.parish_id, userId);
      toast.success("User assigned as parish admin");
      setUserSearch("");
      setUserSearchResults([]);
      // Refresh the details
      const res = await getAdminParishDetails(assignAdminDialog.parish.parish_id);
      setDetailDialog({ open: true, parish: assignAdminDialog.parish, details: res.data });
      setAssignAdminDialog({ open: false, parish: null });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign admin");
    }
    setProcessing(false);
  };

  const openAssignAdminDialog = () => {
    setAssignAdminDialog({ open: true, parish: detailDialog.parish });
    setUserSearch("");
    setUserSearchResults([]);
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
          <Input
            placeholder="Search parishes..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10 bg-white"
          />
        </div>
        <Button onClick={() => setCreateDialog(true)} className="bg-[var(--liturgical-primary)]">
          <Plus className="w-4 h-4 mr-1" /> Add Parish
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-[var(--liturgical-primary)]" /></div>
      ) : (
        <div className="space-y-2">
          {parishes.map((parish) => (
            <Card key={parish.parish_id} className={`bg-white border-[#E8E4DC] ${parish.is_active === false ? 'opacity-60' : ''}`}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[var(--liturgical-primary)] flex items-center justify-center">
                      <Building className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-['Nunito'] font-semibold">{parish.name}</p>
                        {parish.is_active === false && <Badge className="bg-red-100 text-red-800">Suspended</Badge>}
                      </div>
                      <p className="text-xs text-[#6B665E]">
                        {[parish.city, parish.state].filter(Boolean).join(", ") || parish.location || parish.address || parish.diocese || ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{parish.stats?.members || 0} members</Badge>
                    <Badge variant="outline" className="bg-purple-50">{parish.stats?.admins || 0} admins</Badge>
                    <Button variant="ghost" size="sm" onClick={() => viewParishDetails(parish)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    {parish.is_active !== false ? (
                      <Button variant="ghost" size="sm" onClick={() => handleSuspend(parish.parish_id)}>
                        <Power className="w-4 h-4 text-red-500" />
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={() => handleReactivate(parish.parish_id)}>
                        <Power className="w-4 h-4 text-green-500" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Parish Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Cinzel']">Create New Parish</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Parish Name *</Label>
              <Input
                value={newParish.name}
                onChange={(e) => setNewParish({ ...newParish, name: e.target.value })}
                placeholder="St. Mary's Catholic Church"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>City</Label>
                <Input
                  value={newParish.city}
                  onChange={(e) => setNewParish({ ...newParish, city: e.target.value })}
                  placeholder="City"
                />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Input
                  value={newParish.state}
                  onChange={(e) => setNewParish({ ...newParish, state: e.target.value })}
                  placeholder="State"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Country</Label>
                <Input
                  value={newParish.country}
                  onChange={(e) => setNewParish({ ...newParish, country: e.target.value })}
                  placeholder="Country"
                />
              </div>
              <div className="space-y-2">
                <Label>Diocese</Label>
                <Input
                  value={newParish.diocese}
                  onChange={(e) => setNewParish({ ...newParish, diocese: e.target.value })}
                  placeholder="Diocese"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={processing} className="bg-[var(--liturgical-primary)]">
              {processing ? "Creating..." : "Create Parish"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Parish Details Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(open) => setDetailDialog({ ...detailDialog, open })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Cinzel']">{detailDialog.details?.parish?.name}</DialogTitle>
          </DialogHeader>
          {detailDialog.details && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-[#F9F7F2] rounded p-2">
                  <p className="text-lg font-bold">{detailDialog.details.stats.total_members}</p>
                  <p className="text-xs text-[#6B665E]">Members</p>
                </div>
                <div className="bg-[#F9F7F2] rounded p-2">
                  <p className="text-lg font-bold">{detailDialog.details.stats.posts}</p>
                  <p className="text-xs text-[#6B665E]">Posts</p>
                </div>
                <div className="bg-[#F9F7F2] rounded p-2">
                  <p className="text-lg font-bold">{detailDialog.details.stats.events}</p>
                  <p className="text-xs text-[#6B665E]">Events</p>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold">Parish Admins ({detailDialog.details.admins.length})</h4>
                  <Button size="sm" variant="outline" onClick={openAssignAdminDialog} data-testid="assign-admin-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add Admin
                  </Button>
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {detailDialog.details.admins.length === 0 ? (
                    <p className="text-sm text-[#6B665E] text-center py-2">No admins assigned</p>
                  ) : (
                    detailDialog.details.admins.map((admin) => (
                      <div key={admin.user_id} className="flex items-center justify-between p-2 bg-[#F9F7F2] rounded">
                        <div className="flex items-center gap-2">
                          <Avatar className="w-6 h-6">
                            <AvatarImage src={resolveImageUrl(admin.picture)} />
                            <AvatarFallback className="text-xs">{admin.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <span className="text-sm font-medium">{admin.name}</span>
                            <p className="text-xs text-[#6B665E]">{admin.email}</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            await adminRemoveParishAdmin(detailDialog.parish.parish_id, admin.user_id);
                            toast.success("Admin removed");
                            viewParishDetails(detailDialog.parish);
                          }}
                        >
                          <XCircle className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="text-red-600 flex-1" 
                  onClick={() => handleDelete(detailDialog.parish.parish_id)}
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete Parish
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Admin Dialog */}
      <Dialog open={assignAdminDialog.open} onOpenChange={(open) => {
        setAssignAdminDialog({ ...assignAdminDialog, open });
        if (!open) {
          setUserSearch("");
          setUserSearchResults([]);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Cinzel']">Assign Parish Admin</DialogTitle>
            <DialogDescription>
              Search for a user to assign as admin for {assignAdminDialog.parish?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Search User by Name or Email</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
                <Input
                  placeholder="Type to search users..."
                  value={userSearch}
                  onChange={(e) => {
                    setUserSearch(e.target.value);
                    searchUsersForAdmin(e.target.value);
                  }}
                  className="pl-10"
                  data-testid="admin-user-search-input"
                />
              </div>
            </div>

            {/* Search Results */}
            <div className="max-h-64 overflow-y-auto space-y-1">
              {searchingUsers ? (
                <div className="flex justify-center py-4">
                  <RefreshCw className="w-5 h-5 animate-spin text-[var(--liturgical-primary)]" />
                </div>
              ) : userSearchResults.length > 0 ? (
                userSearchResults.map((user) => (
                  <div
                    key={user.user_id}
                    className="flex items-center justify-between p-3 bg-[#F9F7F2] rounded-lg hover:bg-[#E8E4DC] cursor-pointer transition-colors"
                    onClick={() => handleAssignAdmin(user.user_id)}
                    data-testid={`assign-user-${user.user_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={resolveImageUrl(user.picture)} />
                        <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-xs">
                          {user.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-['Nunito'] font-medium text-sm">{user.name}</p>
                        <p className="text-xs text-[#6B665E]">{user.email}</p>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="bg-[var(--liturgical-primary)] text-white hover:brightness-90">
                      <Plus className="w-4 h-4 mr-1" /> Assign
                    </Button>
                  </div>
                ))
              ) : userSearch.trim() ? (
                <p className="text-center text-[#6B665E] py-4">No users found</p>
              ) : (
                <p className="text-center text-[#6B665E] py-4">Start typing to search for users</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignAdminDialog({ open: false, parish: null })}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =============================================================================
// ANALYTICS TAB COMPONENT
// =============================================================================
function AnalyticsTab() {
  const [analytics, setAnalytics] = useState(null);
  const [rankings, setRankings] = useState([]);
  const [contentStats, setContentStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [overviewRes, rankingsRes, contentRes] = await Promise.all([
        getAnalyticsOverview(),
        getParishRankings(10),
        getContentStats()
      ]);
      setAnalytics(overviewRes.data);
      setRankings(rankingsRes.data.rankings);
      setContentStats(contentRes.data);
    } catch {
      toast.error("Failed to load analytics");
    }
    setLoading(false);
  };

  if (loading) {
    return <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-[var(--liturgical-primary)]" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard title="Total Users" value={analytics.users.total} icon={Users} color="bg-blue-500" subtitle={`+${analytics.users.new_30d} this month`} />
          <StatCard title="Active (7d)" value={analytics.users.active_7d} icon={TrendingUp} color="bg-green-500" />
          <StatCard title="Total Posts" value={analytics.content.total_posts} icon={FileText} color="bg-purple-500" subtitle={`+${analytics.content.posts_7d} this week`} />
          <StatCard title="Parishes" value={analytics.parishes.total} icon={Building} color="bg-[var(--liturgical-primary)]" subtitle={`${analytics.parishes.active} active`} />
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Parish Rankings */}
        <Card className="bg-white border-[#E8E4DC]">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Cinzel']">Top Parishes by Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {rankings.map((parish, idx) => (
                <div key={parish.parish_id} className="flex items-center justify-between p-2 bg-[#F9F7F2] rounded">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-[var(--liturgical-primary)] text-white text-xs flex items-center justify-center">
                      {idx + 1}
                    </span>
                    <div>
                      <span className="font-['Nunito'] text-sm">{parish.name}</span>
                      {(parish.location || parish.address || parish.diocese) && (
                        <p className="text-[10px] text-[#6B665E] font-['Nunito']">{parish.location || parish.address || parish.diocese}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold">{parish.activity_score}</p>
                    <p className="text-xs text-[#6B665E]">{parish.members} members</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Content Stats */}
        {contentStats && (
          <Card className="bg-white border-[#E8E4DC]">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-['Cinzel']">Content Breakdown</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold mb-2">Report Reasons</h4>
                <div className="space-y-1">
                  {contentStats.report_reasons.slice(0, 5).map((r) => (
                    <div key={r.reason} className="flex justify-between text-sm">
                      <span className="text-[#6B665E]">{r.reason || "Other"}</span>
                      <span className="font-semibold">{r.count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-semibold mb-2">Listing Categories</h4>
                <div className="space-y-1">
                  {contentStats.listing_categories.slice(0, 5).map((c) => (
                    <div key={c.category} className="flex justify-between text-sm">
                      <span className="text-[#6B665E]">{c.category || "Uncategorized"}</span>
                      <span className="font-semibold">{c.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Moderation Stats */}
      {analytics && (
        <Card className="bg-white border-[#E8E4DC]">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Cinzel']">Moderation Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-2xl font-bold text-red-600">{analytics.moderation.pending_reports}</p>
                <p className="text-sm text-[#6B665E]">Pending Reports</p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg">
                <p className="text-2xl font-bold text-yellow-600">{analytics.moderation.active_bans}</p>
                <p className="text-sm text-[#6B665E]">Active Bans</p>
              </div>
              <div className="p-4 bg-orange-50 rounded-lg">
                <p className="text-2xl font-bold text-orange-600">{analytics.moderation.total_warnings}</p>
                <p className="text-sm text-[#6B665E]">Total Warnings</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// =============================================================================
// ANNOUNCEMENTS TAB COMPONENT
// =============================================================================
function AnnouncementsTab() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: "", message: "", type: "info", link_url: "", link_text: "", is_dismissible: true
  });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    setLoading(true);
    try {
      const res = await getSiteAnnouncements();
      setAnnouncements(res.data.announcements);
    } catch {
      toast.error("Failed to load announcements");
    }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!newAnnouncement.title.trim() || !newAnnouncement.message.trim()) {
      toast.error("Title and message are required");
      return;
    }
    setProcessing(true);
    try {
      await createSiteAnnouncement(newAnnouncement);
      toast.success("Announcement created");
      setCreateDialog(false);
      setNewAnnouncement({ title: "", message: "", type: "info", link_url: "", link_text: "", is_dismissible: true });
      loadAnnouncements();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create announcement");
    }
    setProcessing(false);
  };

  const handleToggle = async (id) => {
    try {
      await toggleAnnouncement(id);
      loadAnnouncements();
    } catch {
      toast.error("Failed to toggle announcement");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteSiteAnnouncement(id);
      toast.success("Announcement deleted");
      loadAnnouncements();
    } catch {
      toast.error("Failed to delete announcement");
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      info: "bg-blue-100 text-blue-800",
      warning: "bg-yellow-100 text-yellow-800",
      success: "bg-green-100 text-green-800",
      error: "bg-red-100 text-red-800"
    };
    return colors[type] || colors.info;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setCreateDialog(true)} className="bg-[var(--liturgical-primary)]">
          <Plus className="w-4 h-4 mr-1" /> New Announcement
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-[var(--liturgical-primary)]" /></div>
      ) : announcements.length === 0 ? (
        <Card className="bg-white border-[#E8E4DC]">
          <CardContent className="py-8 text-center">
            <Megaphone className="w-12 h-12 text-[#6B665E] mx-auto mb-4" />
            <p className="text-[#6B665E]">No announcements yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {announcements.map((ann) => (
            <Card key={ann.announcement_id} className={`bg-white border-[#E8E4DC] ${!ann.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={getTypeColor(ann.type)}>{ann.type}</Badge>
                      {!ann.is_active && <Badge variant="outline">Inactive</Badge>}
                    </div>
                    <h4 className="font-['Nunito'] font-semibold">{ann.title}</h4>
                    <p className="text-sm text-[#6B665E] line-clamp-2">{ann.message}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Switch checked={ann.is_active} onCheckedChange={() => handleToggle(ann.announcement_id)} />
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(ann.announcement_id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Announcement Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Cinzel']">Create Announcement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={newAnnouncement.title}
                onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                placeholder="Announcement title"
              />
            </div>
            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea
                value={newAnnouncement.message}
                onChange={(e) => setNewAnnouncement({ ...newAnnouncement, message: e.target.value })}
                placeholder="Announcement message..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={newAnnouncement.type} onValueChange={(v) => setNewAnnouncement({ ...newAnnouncement, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Info (Blue)</SelectItem>
                  <SelectItem value="warning">Warning (Yellow)</SelectItem>
                  <SelectItem value="success">Success (Green)</SelectItem>
                  <SelectItem value="error">Error (Red)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Link URL (optional)</Label>
                <Input
                  value={newAnnouncement.link_url}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, link_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-2">
                <Label>Link Text</Label>
                <Input
                  value={newAnnouncement.link_text}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, link_text: e.target.value })}
                  placeholder="Learn more"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={newAnnouncement.is_dismissible}
                onCheckedChange={(v) => setNewAnnouncement({ ...newAnnouncement, is_dismissible: v })}
              />
              <Label>Allow users to dismiss</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={processing} className="bg-[var(--liturgical-primary)]">
              {processing ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =============================================================================
// CONTENT TAB COMPONENT  
// =============================================================================
function ContentTab() {
  const [activeSubTab, setActiveSubTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [listings, setListings] = useState([]);
  const [groups, setGroups] = useState([]);
  const [requests, setRequests] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContent();
  }, [activeSubTab]);

  const loadContent = async (searchQuery = "") => {
    setLoading(true);
    try {
      if (activeSubTab === "posts") {
        const res = await getAdminPosts({ search: searchQuery, limit: 50 });
        setPosts(res.data.posts);
      } else if (activeSubTab === "listings") {
        const res = await getAdminListings({ search: searchQuery, limit: 50 });
        setListings(res.data.listings);
      } else if (activeSubTab === "groups") {
        const res = await getAdminGroups({ search: searchQuery, limit: 50 });
        setGroups(res.data.groups);
      } else if (activeSubTab === "requests") {
        const res = await getAdminRequests({ search: searchQuery, limit: 50 });
        setRequests(res.data.requests);
      }
    } catch {
      toast.error("Failed to load content");
    }
    setLoading(false);
  };

  const handleSearch = (value) => {
    setSearch(value);
    setTimeout(() => loadContent(value), 300);
  };

  const handleDeletePost = async (postId) => {
    if (!confirm("Delete this post?")) return;
    try {
      await deletePostAdmin(postId);
      toast.success("Post deleted");
      loadContent(search);
    } catch {
      toast.error("Failed to delete post");
    }
  };

  const handleDeleteListing = async (listingId) => {
    if (!confirm("Delete this listing?")) return;
    try {
      await deleteListingAdmin(listingId);
      toast.success("Listing deleted");
      loadContent(search);
    } catch {
      toast.error("Failed to delete listing");
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!confirm("Delete this group and all its content?")) return;
    try {
      await deleteGroupAdmin(groupId);
      toast.success("Group deleted");
      loadContent(search);
    } catch {
      toast.error("Failed to delete group");
    }
  };

  const handleSuspendBusiness = async (businessId) => {
    if (!confirm("Suspend this business listing?")) return;
    try {
      await suspendBusinessAdmin(businessId);
      toast.success("Business suspended");
      loadContent(search);
    } catch {
      toast.error("Failed to suspend business");
    }
  };

  const handleReactivateBusiness = async (businessId) => {
    try {
      await reactivateBusinessAdmin(businessId);
      toast.success("Business reactivated");
      loadContent(search);
    } catch {
      toast.error("Failed to reactivate business");
    }
  };

  const handleDeleteBusiness = async (businessId) => {
    if (!confirm("Delete this business listing and all its reviews? This cannot be undone.")) return;
    try {
      await deleteBusinessAdmin(businessId);
      toast.success("Business deleted");
      loadContent(search);
    } catch {
      toast.error("Failed to delete business");
    }
  };

  const handleSuspendRequest = async (requestId) => {
    if (!confirm("Suspend this request? It will be hidden from public view.")) return;
    try {
      await suspendRequestAdmin(requestId);
      toast.success("Request suspended");
      loadContent(search);
    } catch {
      toast.error("Failed to suspend request");
    }
  };

  const handleReactivateRequest = async (requestId) => {
    try {
      await reactivateRequestAdmin(requestId);
      toast.success("Request reactivated");
      loadContent(search);
    } catch {
      toast.error("Failed to reactivate request");
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!confirm("Delete this request permanently? This cannot be undone.")) return;
    try {
      await deleteRequestAdmin(requestId);
      toast.success("Request deleted");
      loadContent(search);
    } catch {
      toast.error("Failed to delete request");
    }
  };

  const getRequestTypeLabel = (type) => {
    const types = {
      prayer: "Prayer Request",
      donation: "Donation Request",
      item: "Item Request",
      service: "Service Request"
    };
    return types[type] || type;
  };

  const getRequestStatusBadge = (status) => {
    const variants = {
      open: "bg-green-100 text-green-700",
      fulfilled: "bg-blue-100 text-blue-700",
      closed: "bg-gray-100 text-gray-700",
      suspended: "bg-red-100 text-red-700"
    };
    return variants[status] || "bg-gray-100 text-gray-700";
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 mb-4">
        <Button
          variant={activeSubTab === "posts" ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveSubTab("posts")}
          className={activeSubTab === "posts" ? "bg-[var(--liturgical-primary)]" : ""}
        >
          <FileText className="w-4 h-4 mr-1" /> Posts
        </Button>
        <Button
          variant={activeSubTab === "listings" ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveSubTab("listings")}
          className={activeSubTab === "listings" ? "bg-[var(--liturgical-primary)]" : ""}
        >
          Items for Sale
        </Button>
        <Button
          variant={activeSubTab === "requests" ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveSubTab("requests")}
          className={activeSubTab === "requests" ? "bg-[var(--liturgical-primary)]" : ""}
        >
          <HandHeart className="w-4 h-4 mr-1" /> Requests
        </Button>
        <Button
          variant={activeSubTab === "groups" ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveSubTab("groups")}
          className={activeSubTab === "groups" ? "bg-[var(--liturgical-primary)]" : ""}
        >
          Groups
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
        <Input
          placeholder={`Search ${activeSubTab}...`}
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          className="pl-10 bg-white"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-[var(--liturgical-primary)]" /></div>
      ) : (
        <div className="space-y-2">
          {activeSubTab === "posts" && posts.map((post) => (
            <Card key={post.post_id} className="bg-white border-[#E8E4DC]">
              <CardContent className="p-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Avatar className="w-6 h-6">
                        <AvatarImage src={resolveImageUrl(post.author?.picture)} />
                        <AvatarFallback className="text-xs">{post.author?.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-semibold">{post.author?.name}</span>
                      <span className="text-xs text-[#6B665E]">{new Date(post.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-sm text-[#2C2C2C] line-clamp-2">{post.content}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDeletePost(post.post_id)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}

          {activeSubTab === "listings" && listings.map((listing) => (
            <Card key={listing.listing_id} className="bg-white border-[#E8E4DC]">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{listing.title}</p>
                    <p className="text-sm text-[#6B665E]">
                      ${listing.price} • {listing.seller?.name}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{listing.status}</Badge>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteListing(listing.listing_id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {activeSubTab === "groups" && groups.map((group) => (
            <Card key={group.group_id} className="bg-white border-[#E8E4DC]">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{group.name}</p>
                    <p className="text-sm text-[#6B665E]">
                      {group.member_count} members • Created by {group.creator?.name}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{group.visibility}</Badge>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteGroup(group.group_id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {activeSubTab === "requests" && requests.map((request) => (
            <Card key={request.request_id} className="bg-white border-[#E8E4DC]">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold line-clamp-1">{request.title || request.content?.slice(0, 50) || "Untitled"}</p>
                      <Badge className={getRequestStatusBadge(request.status)}>
                        {request.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-[#6B665E]">
                      {getRequestTypeLabel(request.request_type)} • by {request.author_name || "Unknown"}
                    </p>
                    <p className="text-xs text-[#6B665E] mt-1 line-clamp-2">
                      {request.content?.slice(0, 100)}...
                    </p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-[#6B665E]">
                      <span>{request.pray_count || 0} praying</span>
                      <span>{request.help_count || 0} helping</span>
                      <span>{new Date(request.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {request.status === "suspended" ? (
                      <Button variant="ghost" size="sm" onClick={() => handleReactivateRequest(request.request_id)} title="Reactivate">
                        <Play className="w-4 h-4 text-green-500" />
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={() => handleSuspendRequest(request.request_id)} title="Suspend">
                        <Pause className="w-4 h-4 text-yellow-500" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteRequest(request.request_id)} title="Delete">
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


// =============================================================================
// DIRECTORY ADMIN TAB COMPONENT
// =============================================================================
function DirectoryAdminTab() {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("unclaimed"); // "unclaimed" | "all"
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editListing, setEditListing] = useState(null);
  const [assignDialog, setAssignDialog] = useState(null);
  const [categories, setCategories] = useState([]);

  const loadListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (searchQuery) params.search = searchQuery;
      const res = await getAdminBusinesses(params);
      setListings(res.data?.businesses || res.data?.listings || []);
    } catch {}
    setLoading(false);
  }, [searchQuery]);

  useEffect(() => { loadListings(); }, [loadListings]);
  useEffect(() => { getBusinessCategories().then(r => setCategories(r.data?.categories || [])).catch(() => {}); }, []);

  const filtered = filter === "unclaimed"
    ? listings.filter(l => l.is_unclaimed || !l.owner_id)
    : listings;

  const displayListings = searchQuery
    ? filtered
    : filtered;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26]">Business Directory</h2>
        <Button onClick={() => { setEditListing(null); setShowCreateModal(true); }}
          className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']">
          <Plus className="w-4 h-4 mr-1.5" /> Add Listing
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
          <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.key === "Enter" && loadListings()}
            placeholder="Search listings..." className="pl-9 border-[#E5E0D5] font-['Nunito'] text-sm" />
        </div>
        <div className="flex gap-1 bg-white rounded-lg border border-[#E5E0D5] p-1">
          <Button variant={filter === "unclaimed" ? "default" : "ghost"} size="sm"
            onClick={() => setFilter("unclaimed")}
            className={`font-['Nunito'] text-xs ${filter === "unclaimed" ? "bg-[var(--liturgical-primary)] text-white" : ""}`}>
            Unclaimed
          </Button>
          <Button variant={filter === "all" ? "default" : "ghost"} size="sm"
            onClick={() => setFilter("all")}
            className={`font-['Nunito'] text-xs ${filter === "all" ? "bg-[var(--liturgical-primary)] text-white" : ""}`}>
            All ({listings.length})
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[var(--liturgical-primary)]" /></div>
      ) : displayListings.length === 0 ? (
        <Card className="border-[#E5E0D5]"><CardContent className="py-12 text-center">
          <Building2 className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
          <p className="text-[#6B665E] font-['Nunito']">{filter === "unclaimed" ? "No unclaimed listings" : "No listings found"}</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {displayListings.map(listing => (
            <Card key={listing.listing_id} className="border-[#E5E0D5] bg-white hover:shadow-md transition-all">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[var(--liturgical-primary)]/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-[var(--liturgical-primary)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-['Nunito'] font-semibold text-sm text-[#2D2A26] truncate">{listing.name}</h3>
                      {(listing.is_unclaimed || !listing.owner_id) && (
                        <Badge className="bg-amber-100 text-amber-800 text-[10px] font-['Nunito']">Unclaimed</Badge>
                      )}
                    </div>
                    <p className="text-xs text-[#6B665E] font-['Nunito']">
                      {listing.category}{listing.owner_name ? ` · ${listing.owner_name}` : ""}{listing.address ? ` · ${listing.address}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {(listing.is_unclaimed || !listing.owner_id) && (
                      <Button variant="outline" size="sm" onClick={() => setAssignDialog(listing)}
                        className="text-xs font-['Nunito'] border-[#E5E0D5]" title="Assign owner">
                        <UserPlus className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => { setEditListing(listing); setShowCreateModal(true); }}
                      className="text-xs font-['Nunito'] border-[#E5E0D5]" title="Edit">
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/directory/${listing.listing_id}`)}
                      className="text-xs font-['Nunito']" title="View">
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <DirectoryFormModal
        isOpen={showCreateModal}
        onClose={() => { setShowCreateModal(false); setEditListing(null); }}
        onSuccess={() => { setShowCreateModal(false); setEditListing(null); loadListings(); }}
        listing={editListing}
        categories={categories}
      />

      {/* Assign Owner Dialog */}
      {assignDialog && (
        <AssignOwnerDialog
          listing={assignDialog}
          onClose={() => setAssignDialog(null)}
          onSuccess={() => { setAssignDialog(null); loadListings(); }}
        />
      )}
    </div>
  );
}

function DirectoryFormModal({ isOpen, onClose, onSuccess, listing, categories }) {
  const isEdit = !!listing;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", category: "", description: "", service_area: "", address: "",
    phone: "", email: "", website: "", about_business: "",
  });

  useEffect(() => {
    if (isOpen) {
      if (listing) {
        setForm({
          name: listing.name || "", category: listing.category || "",
          description: listing.description?.replace(/<[^>]*>/g, '') || "",
          service_area: listing.service_area || "", address: listing.address || "",
          phone: listing.phone || "", email: listing.email || "",
          website: listing.website || "", about_business: listing.about_business || "",
        });
      } else {
        setForm({ name: "", category: "", description: "", service_area: "", address: "", phone: "", email: "", website: "", about_business: "" });
      }
    }
  }, [isOpen, listing]);

  const handleSubmit = async () => {
    if (!form.name || !form.category || !form.description) {
      toast.error("Name, category, and description are required");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, description: `<p>${form.description}</p>` };
      if (isEdit) {
        await adminUpdateListing(listing.listing_id, payload);
        toast.success("Listing updated");
      } else {
        await adminCreateListing(payload);
        toast.success("Listing created");
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save listing");
    }
    setLoading(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-xl">{isEdit ? "Edit Listing" : "Add Business Listing"}</DialogTitle>
          {!isEdit && <DialogDescription className="font-['Nunito'] text-sm">Create an unclaimed listing that businesses can claim later.</DialogDescription>}
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="font-['Nunito'] text-sm">Business Name *</Label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="border-[#E5E0D5]" />
          </div>
          <div>
            <Label className="font-['Nunito'] text-sm">Category *</Label>
            <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
              <SelectTrigger className="border-[#E5E0D5]"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent className="bg-white max-h-60">
                {categories.map(c => <SelectItem key={c} value={c} className="font-['Nunito']">{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-['Nunito'] text-sm">Description *</Label>
            <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="border-[#E5E0D5] min-h-[80px]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-['Nunito'] text-sm">Phone</Label>
              <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="border-[#E5E0D5]" />
            </div>
            <div>
              <Label className="font-['Nunito'] text-sm">Email</Label>
              <Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="border-[#E5E0D5]" />
            </div>
          </div>
          <div>
            <Label className="font-['Nunito'] text-sm">Website</Label>
            <Input value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} className="border-[#E5E0D5]" />
          </div>
          <div>
            <Label className="font-['Nunito'] text-sm">Address</Label>
            <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className="border-[#E5E0D5]" />
          </div>
          <div>
            <Label className="font-['Nunito'] text-sm">Service Area</Label>
            <Input value={form.service_area} onChange={e => setForm(f => ({ ...f, service_area: e.target.value }))} className="border-[#E5E0D5]" placeholder="e.g. Memphis, TN" />
          </div>
          <div>
            <Label className="font-['Nunito'] text-sm">About the Business</Label>
            <Textarea value={form.about_business} onChange={e => setForm(f => ({ ...f, about_business: e.target.value }))} className="border-[#E5E0D5]" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E5E0D5] font-['Nunito']">Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
            {isEdit ? "Save Changes" : "Create Listing"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignOwnerDialog({ listing, onClose, onSuccess }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const res = await searchUsers(query.trim(), { limit: 10 });
      setResults(res.data?.users || res.data || []);
    } catch {}
    setSearching(false);
  };

  const handleAssign = async (userId, userName) => {
    setAssigning(true);
    try {
      await adminAssignOwner(listing.listing_id, userId);
      toast.success(`Listing assigned to ${userName}`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign");
    }
    setAssigning(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-lg">Assign Owner</DialogTitle>
          <DialogDescription className="font-['Nunito'] text-sm">
            Search for a user to assign as the owner of <strong>{listing.name}</strong>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input value={query} onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSearch()}
              placeholder="Search by name..." className="border-[#E5E0D5] font-['Nunito'] text-sm" />
            <Button onClick={handleSearch} disabled={searching} className="bg-[var(--liturgical-primary)] text-white font-['Nunito']">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
            </Button>
          </div>
          {results.length > 0 && (
            <div className="space-y-1 max-h-60 overflow-y-auto">
              {results.map(u => (
                <div key={u.user_id} className="flex items-center justify-between p-2 rounded-lg hover:bg-[#F9F7F2]">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={resolveImageUrl(u.picture)} />
                      <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-xs">{u.name?.[0]}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-['Nunito'] text-sm font-semibold">{u.name}</p>
                      {u.parish_name && <p className="text-[10px] text-[#6B665E] font-['Nunito']">{u.parish_name}</p>}
                    </div>
                  </div>
                  <Button size="sm" onClick={() => handleAssign(u.user_id, u.name)} disabled={assigning}
                    className="bg-[var(--liturgical-primary)] text-white font-['Nunito'] text-xs">
                    Assign
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// =============================================================================
// SETTINGS TAB COMPONENT
// =============================================================================
function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await getSystemSettings();
      setSettings(res.data.settings);
    } catch {
      toast.error("Failed to load settings");
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSystemSettings(settings);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save settings");
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-[var(--liturgical-primary)]" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card className="bg-white border-[#E8E4DC]">
        <CardHeader>
          <CardTitle className="text-lg font-['Cinzel']">Registration Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Registration Mode</Label>
            <Select value={settings.registration_mode} onValueChange={(v) => setSettings({ ...settings, registration_mode: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open (Anyone can register)</SelectItem>
                <SelectItem value="invite_only">Invite Only</SelectItem>
                <SelectItem value="closed">Closed (No new registrations)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label>Require Email Verification</Label>
              <p className="text-xs text-[#6B665E]">Users must verify email before accessing features</p>
            </div>
            <Switch
              checked={settings.require_email_verification}
              onCheckedChange={(v) => setSettings({ ...settings, require_email_verification: v })}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E8E4DC]">
        <CardHeader>
          <CardTitle className="text-lg font-['Cinzel']">Content Limits</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Max Posts Per Day</Label>
              <Input
                type="number"
                value={settings.max_posts_per_day}
                onChange={(e) => setSettings({ ...settings, max_posts_per_day: parseInt(e.target.value) })}
              />
            </div>
            <div className="space-y-2">
              <Label>Max Listings Per User</Label>
              <Input
                type="number"
                value={settings.max_listings_per_user}
                onChange={(e) => setSettings({ ...settings, max_listings_per_user: parseInt(e.target.value) })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E8E4DC]">
        <CardHeader>
          <CardTitle className="text-lg font-['Cinzel']">Moderation Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Content Moderation</Label>
              <p className="text-xs text-[#6B665E]">Enable reporting and moderation features</p>
            </div>
            <Switch
              checked={settings.content_moderation_enabled}
              onCheckedChange={(v) => setSettings({ ...settings, content_moderation_enabled: v })}
            />
          </div>
          <div className="space-y-2">
            <Label>Auto-Ban Threshold</Label>
            <p className="text-xs text-[#6B665E]">Number of warnings before automatic ban</p>
            <Input
              type="number"
              value={settings.auto_ban_threshold}
              onChange={(e) => setSettings({ ...settings, auto_ban_threshold: parseInt(e.target.value) })}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-[var(--liturgical-primary)]">
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// INVITES TAB COMPONENT
// =============================================================================
function InvitesTab() {
  const [invites, setInvites] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadInvites();
    loadStats();
  }, [page]);

  const loadInvites = async () => {
    setLoading(true);
    try {
      const res = await getAdminInvites(page);
      setInvites(res.data.invites);
      setTotalPages(res.data.pages);
    } catch {
      toast.error("Failed to load invites");
    }
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      const res = await getInviteStats();
      setStats(res.data);
    } catch {}
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading && page === 1) {
    return <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-[var(--liturgical-primary)]" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard 
            title="Total Invites" 
            value={stats.total_invites} 
            icon={Ticket} 
            color="bg-blue-500" 
          />
          <StatCard 
            title="Used Invites" 
            value={stats.used_invites} 
            icon={UserPlus} 
            color="bg-green-500" 
            subtitle={`${stats.conversion_rate}% conversion`}
          />
          <StatCard 
            title="Pending" 
            value={stats.pending_invites} 
            icon={Clock} 
            color="bg-yellow-500" 
          />
          <StatCard 
            title="Expired" 
            value={stats.expired_invites} 
            icon={XCircle} 
            color="bg-red-500" 
          />
        </div>
      )}

      {/* Top Inviters */}
      {stats?.top_inviters?.length > 0 && (
        <Card className="bg-white border-[#E8E4DC]">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Cinzel']">Top Inviters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.top_inviters.map((inviter, idx) => (
                <div key={inviter.user_id} className="flex items-center justify-between p-2 bg-[#F9F7F2] rounded">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-[var(--liturgical-primary)] text-white text-xs flex items-center justify-center">
                      {idx + 1}
                    </span>
                    <span className="font-['Nunito'] text-sm">{inviter.name}</span>
                  </div>
                  <Badge variant="outline">{inviter.successful_invites} invites</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invites List - Inviter → Invitee */}
      <Card className="bg-white border-[#E8E4DC]">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-['Cinzel']">Invite History</CardTitle>
        </CardHeader>
        <CardContent>
          {invites.length === 0 ? (
            <div className="text-center py-8">
              <Ticket className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
              <p className="text-[#6B665E] font-['Nunito']">No invites yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {invites.map((invite) => (
                <div 
                  key={invite.invite_code} 
                  className={`p-3 rounded-lg border ${
                    invite.is_used 
                      ? 'bg-green-50 border-green-200' 
                      : new Date(invite.expires_at) < new Date() 
                        ? 'bg-red-50 border-red-200 opacity-60'
                        : 'bg-white border-[#E8E4DC]'
                  }`}
                  data-testid={`invite-row-${invite.invite_code}`}
                >
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      {/* Inviter */}
                      <div className="flex items-center gap-2">
                        <Avatar className="w-8 h-8">
                          <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-xs">
                            {invite.inviter_name?.charAt(0) || "?"}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-['Nunito'] font-medium text-sm">{invite.inviter_name}</p>
                          <p className="text-xs text-[#6B665E]">{invite.inviter_email}</p>
                        </div>
                      </div>

                      {/* Arrow */}
                      <ArrowRight className="w-4 h-4 text-[#6B665E]" />

                      {/* Invitee (if used) or status */}
                      {invite.is_used ? (
                        <div className="flex items-center gap-2">
                          <Avatar className="w-8 h-8">
                            <AvatarFallback className="bg-green-600 text-white text-xs">
                              {invite.used_by_name?.charAt(0) || "?"}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-['Nunito'] font-medium text-sm">{invite.used_by_name}</p>
                            <p className="text-xs text-[#6B665E]">{invite.used_by_email}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-[#6B665E] font-['Nunito']">
                          {new Date(invite.expires_at) < new Date() ? (
                            <span className="text-red-600">Expired</span>
                          ) : (
                            <span className="text-yellow-600">Pending</span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="text-right text-xs text-[#6B665E] font-['Nunito']">
                      {invite.is_used ? (
                        <>
                          <p>Used: {formatDate(invite.used_at)}</p>
                        </>
                      ) : (
                        <>
                          <p>Created: {formatDate(invite.created_at)}</p>
                          <p>Expires: {formatDate(invite.expires_at)}</p>
                        </>
                      )}
                    </div>
                  </div>
                  {invite.note && (
                    <p className="text-xs text-[#6B665E] mt-2 italic">Note: {invite.note}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button 
                variant="outline" 
                size="sm" 
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </Button>
              <span className="px-3 py-1 text-sm">Page {page} of {totalPages}</span>
              <Button 
                variant="outline" 
                size="sm" 
                disabled={page === totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// =============================================================================
// SUPPORT TICKETS TAB COMPONENT
// =============================================================================
function SupportTab() {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadTickets();
    loadStats();
  }, [statusFilter, page]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const res = await getAdminSupportTickets(statusFilter || undefined, page);
      setTickets(res.data.tickets);
      setTotalPages(res.data.pages);
    } catch {
      toast.error("Failed to load tickets");
    }
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      const res = await getSupportTicketStats();
      setStats(res.data);
    } catch {}
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    setUpdating(true);
    try {
      await updateSupportTicket(ticketId, { status: newStatus });
      toast.success("Ticket updated");
      loadTickets();
      loadStats();
      setSelectedTicket(null);
    } catch {
      toast.error("Failed to update ticket");
    }
    setUpdating(false);
  };

  const handleAddNotes = async (ticketId) => {
    if (!adminNotes.trim()) return;
    setUpdating(true);
    try {
      await updateSupportTicket(ticketId, { admin_notes: adminNotes });
      toast.success("Notes saved");
      loadTickets();
      setAdminNotes("");
    } catch {
      toast.error("Failed to save notes");
    }
    setUpdating(false);
  };

  const handleDelete = async (ticketId) => {
    if (!window.confirm("Delete this ticket?")) return;
    try {
      await deleteSupportTicket(ticketId);
      toast.success("Ticket deleted");
      loadTickets();
      loadStats();
    } catch {
      toast.error("Failed to delete ticket");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const statusColors = {
    open: "bg-red-100 text-red-800",
    in_progress: "bg-yellow-100 text-yellow-800",
    resolved: "bg-green-100 text-green-800",
    closed: "bg-gray-100 text-gray-600"
  };

  const categoryConfig = {
    claim_listing: { label: "Listing Claims", icon: Store, color: "text-green-600", bgColor: "bg-green-50", borderColor: "border-green-200" },
    bug: { label: "Something is Broken", icon: Bug, color: "text-red-500", bgColor: "bg-red-50", borderColor: "border-red-200" },
    feature: { label: "Feature Requests", icon: Lightbulb, color: "text-amber-500", bgColor: "bg-amber-50", borderColor: "border-amber-200" },
    other: { label: "Other", icon: MessageSquare, color: "text-blue-500", bgColor: "bg-blue-50", borderColor: "border-blue-200" },
    general: { label: "General", icon: HelpCircle, color: "text-gray-500", bgColor: "bg-gray-50", borderColor: "border-gray-200" },
    account: { label: "Account Issues", icon: UserX, color: "text-purple-500", bgColor: "bg-purple-50", borderColor: "border-purple-200" }
  };

  // Group tickets by category
  const groupedTickets = {
    claim_listing: tickets.filter(t => t.category === "claim_listing"),
    bug: tickets.filter(t => t.category === "bug"),
    feature: tickets.filter(t => t.category === "feature"),
    other: tickets.filter(t => t.category === "other" || t.category === "general" || t.category === "account" || !t.category)
  };

  // Get filtered tickets based on category filter
  const filteredTickets = categoryFilter === "all" ? tickets : groupedTickets[categoryFilter] || [];

  // Count unresolved per category
  const getCategoryUnresolved = (category) => {
    const categoryTickets = category === "other" 
      ? tickets.filter(t => t.category === "other" || t.category === "general" || t.category === "account" || !t.category)
      : tickets.filter(t => t.category === category);
    return categoryTickets.filter(t => t.status === "open" || t.status === "in_progress").length;
  };

  if (loading && page === 1) {
    return <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-[var(--liturgical-primary)]" /></div>;
  }

  const renderTicketCard = (ticket) => {
    const config = categoryConfig[ticket.category] || categoryConfig.other;
    const IconComponent = config.icon;
    
    return (
      <div 
        key={ticket.ticket_id}
        className={`p-4 rounded-lg border cursor-pointer transition-colors ${
          selectedTicket?.ticket_id === ticket.ticket_id 
            ? 'bg-[var(--liturgical-primary)]/5 border-[var(--liturgical-primary)]' 
            : `bg-white border-[#E8E4DC] hover:${config.bgColor}`
        }`}
        onClick={() => setSelectedTicket(selectedTicket?.ticket_id === ticket.ticket_id ? null : ticket)}
        data-testid={`ticket-${ticket.ticket_id}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <IconComponent className={`w-4 h-4 ${config.color} flex-shrink-0`} />
              <span className="font-['Nunito'] font-semibold text-sm truncate">{ticket.subject}</span>
              <Badge className={`${statusColors[ticket.status]} border-0 text-xs`}>
                {ticket.status.replace('_', ' ')}
              </Badge>
            </div>
            <p className="text-xs text-[#6B665E] font-['Nunito'] mb-1">
              From: {ticket.user_name} ({ticket.user_email}) • {formatDate(ticket.created_at)}
            </p>
            <p className="text-sm text-[#2D2A26] font-['Nunito'] line-clamp-2">{ticket.message}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
            onClick={(e) => { e.stopPropagation(); handleDelete(ticket.ticket_id); }}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Expanded view */}
        {selectedTicket?.ticket_id === ticket.ticket_id && (
          <div className="mt-4 pt-4 border-t border-[#E5E0D5] space-y-4">
            <div>
              <p className="text-xs text-[#6B665E] font-['Nunito'] font-semibold uppercase mb-1">Full Message</p>
              <p className="text-sm text-[#2D2A26] whitespace-pre-wrap">{ticket.message}</p>
            </div>
            
            {ticket.admin_notes && (
              <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                <p className="text-xs text-yellow-700 font-['Nunito'] font-semibold uppercase mb-1">Admin Notes</p>
                <p className="text-sm text-yellow-800 whitespace-pre-wrap">{ticket.admin_notes}</p>
              </div>
            )}
            
            {/* Status actions */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-[#6B665E] font-['Nunito']">Update status:</span>
              {["open", "in_progress", "resolved", "closed"].map(status => (
                <Button
                  key={status}
                  size="sm"
                  variant={ticket.status === status ? "default" : "outline"}
                  className={ticket.status === status ? "bg-[var(--liturgical-primary)]" : "border-[#E5E0D5]"}
                  disabled={updating || ticket.status === status}
                  onClick={(e) => { e.stopPropagation(); handleUpdateStatus(ticket.ticket_id, status); }}
                >
                  {status.replace('_', ' ')}
                </Button>
              ))}
            </div>
            
            {/* Add notes */}
            <div className="space-y-2">
              <Textarea
                placeholder="Add admin notes..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="text-sm border-[#E5E0D5]"
                onClick={(e) => e.stopPropagation()}
              />
              <Button
                size="sm"
                onClick={(e) => { e.stopPropagation(); handleAddNotes(ticket.ticket_id); }}
                disabled={updating || !adminNotes.trim()}
                className="bg-[var(--liturgical-primary)]"
              >
                Save Notes
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard title="Total" value={stats.total} icon={MessageSquare} color="bg-blue-500" />
          <StatCard title="Open" value={stats.open} icon={AlertTriangle} color="bg-red-500" />
          <StatCard title="In Progress" value={stats.in_progress} icon={Clock} color="bg-yellow-500" />
          <StatCard title="Resolved" value={stats.resolved} icon={CheckCircle} color="bg-green-500" />
          <StatCard title="Closed" value={stats.closed} icon={XCircle} color="bg-gray-500" />
        </div>
      )}

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={categoryFilter === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setCategoryFilter("all")}
          className={categoryFilter === "all" ? "bg-[var(--liturgical-primary)]" : "border-[#E5E0D5]"}
          data-testid="category-filter-all"
        >
          All Tickets ({tickets.length})
        </Button>
        <Button
          variant={categoryFilter === "claim_listing" ? "default" : "outline"}
          size="sm"
          onClick={() => setCategoryFilter("claim_listing")}
          className={categoryFilter === "claim_listing" ? "bg-green-600 hover:bg-green-700" : "border-green-200 text-green-600 hover:bg-green-50"}
        >
          <Store className="w-3.5 h-3.5 mr-1" /> Claims ({getCategoryUnresolved("claim_listing")})
        </Button>
        <Button
          variant={categoryFilter === "bug" ? "default" : "outline"}
          size="sm"
          onClick={() => setCategoryFilter("bug")}
          className={categoryFilter === "bug" ? "bg-red-500 hover:bg-red-600" : "border-red-200 text-red-600 hover:bg-red-50"}
          data-testid="category-filter-bug"
        >
          <Bug className="w-4 h-4 mr-1" />
          Something is Broken ({groupedTickets.bug.length})
          {getCategoryUnresolved("bug") > 0 && (
            <span className="ml-1 w-5 h-5 bg-red-600 text-white text-[10px] rounded-full flex items-center justify-center">
              {getCategoryUnresolved("bug")}
            </span>
          )}
        </Button>
        <Button
          variant={categoryFilter === "feature" ? "default" : "outline"}
          size="sm"
          onClick={() => setCategoryFilter("feature")}
          className={categoryFilter === "feature" ? "bg-amber-500 hover:bg-amber-600" : "border-amber-200 text-amber-600 hover:bg-amber-50"}
          data-testid="category-filter-feature"
        >
          <Lightbulb className="w-4 h-4 mr-1" />
          Feature Requests ({groupedTickets.feature.length})
          {getCategoryUnresolved("feature") > 0 && (
            <span className="ml-1 w-5 h-5 bg-amber-600 text-white text-[10px] rounded-full flex items-center justify-center">
              {getCategoryUnresolved("feature")}
            </span>
          )}
        </Button>
        <Button
          variant={categoryFilter === "other" ? "default" : "outline"}
          size="sm"
          onClick={() => setCategoryFilter("other")}
          className={categoryFilter === "other" ? "bg-blue-500 hover:bg-blue-600" : "border-blue-200 text-blue-600 hover:bg-blue-50"}
          data-testid="category-filter-other"
        >
          <MessageSquare className="w-4 h-4 mr-1" />
          Other ({groupedTickets.other.length})
          {getCategoryUnresolved("other") > 0 && (
            <span className="ml-1 w-5 h-5 bg-blue-600 text-white text-[10px] rounded-full flex items-center justify-center">
              {getCategoryUnresolved("other")}
            </span>
          )}
        </Button>
      </div>

      {/* Status Filter */}
      <div className="flex items-center gap-4">
        <Select value={statusFilter || "all"} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-[180px] border-[#E5E0D5]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tickets List */}
      <Card className="bg-white border-[#E8E4DC]">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-['Cinzel'] flex items-center gap-2">
            {categoryFilter !== "all" && categoryConfig[categoryFilter] && (
              <>
                {(() => {
                  const Icon = categoryConfig[categoryFilter].icon;
                  return <Icon className={`w-5 h-5 ${categoryConfig[categoryFilter].color}`} />;
                })()}
                {categoryConfig[categoryFilter].label}
              </>
            )}
            {categoryFilter === "all" && "All Support Tickets"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredTickets.length === 0 ? (
            <div className="text-center py-8">
              <HelpCircle className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
              <p className="text-[#6B665E] font-['Nunito']">
                {categoryFilter === "all" ? "No support tickets yet" : `No ${categoryConfig[categoryFilter]?.label.toLowerCase() || categoryFilter} tickets`}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredTickets.map(renderTicketCard)}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="px-3 py-1 text-sm font-['Nunito']">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// JOIN REQUESTS TAB COMPONENT (CONSOLIDATED PARISH JOIN REQUESTS)
// =============================================================================
function JoinRequestsTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const res = await getAdminJoinRequests("pending");
      setRequests(res.data.requests || []);
    } catch (err) {
      toast.error("Failed to load join requests");
    }
    setLoading(false);
  };

  const handleApprove = async (membershipId) => {
    setProcessing(prev => ({ ...prev, [membershipId]: true }));
    try {
      await adminApproveJoinRequest(membershipId);
      toast.success("Membership approved!");
      setRequests(requests.filter(r => r.membership_id !== membershipId));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve request");
    }
    setProcessing(prev => ({ ...prev, [membershipId]: false }));
  };

  const handleReject = async (membershipId) => {
    setProcessing(prev => ({ ...prev, [membershipId]: true }));
    try {
      await adminRejectJoinRequest(membershipId);
      toast.success("Membership rejected");
      setRequests(requests.filter(r => r.membership_id !== membershipId));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject request");
    }
    setProcessing(prev => ({ ...prev, [membershipId]: false }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  if (loading) {
    return <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-[var(--liturgical-primary)]" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="join-requests-tab">
      <Card className="bg-white border-[#E8E4DC]">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-['Cinzel'] flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-[var(--liturgical-primary)]" />
            Parish Join Requests
          </CardTitle>
          <p className="text-sm text-[#6B665E] font-['Nunito']">
            Manage membership requests across all parishes
          </p>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-['Cinzel'] font-semibold text-[#2C2C2C] mb-2">All Caught Up!</h3>
              <p className="text-[#6B665E] font-['Nunito']">No pending parish join requests to review.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map((req) => (
                <div 
                  key={req.membership_id} 
                  className="flex items-center gap-4 p-4 bg-[#F9F7F2] rounded-lg border border-[#E5E0D5]"
                  data-testid={`join-request-${req.membership_id}`}
                >
                  <Avatar className="h-12 w-12 border-2 border-[#E5E0D5]">
                    <AvatarImage src={resolveImageUrl(req.user?.picture || req.user?.profile_picture)} />
                    <AvatarFallback className="bg-[var(--liturgical-primary)] text-white">
                      {req.user?.name?.[0]?.toUpperCase() || "?"}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-['Nunito'] font-semibold text-[#2D2A26]">{req.user?.name || req.user_name}</p>
                      <Badge variant="outline" className="text-xs border-[var(--liturgical-primary)] text-[var(--liturgical-primary)]">
                        <Building className="w-3 h-3 mr-1" />
                        {req.parish?.name || "Unknown Parish"}
                      </Badge>
                    </div>
                    <p className="text-xs text-[#6B665E] font-['Nunito']">{req.user?.email || req.user_email}</p>
                    {req.parish?.city && req.parish?.state && (
                      <p className="text-xs text-[#6B665E] font-['Nunito'] mt-0.5">
                        <MapPin className="w-3 h-3 inline mr-1" />
                        {req.parish.city}, {req.parish.state}
                      </p>
                    )}
                    <p className="text-[10px] text-[#6B665E] font-['Nunito'] mt-1">
                      <Clock className="w-3 h-3 inline mr-1" />
                      Requested {formatDate(req.created_at)}
                    </p>
                  </div>
                  
                  <div className="flex gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      onClick={() => handleApprove(req.membership_id)}
                      disabled={processing[req.membership_id]}
                      className="bg-green-600 hover:bg-green-700 text-white font-['Nunito'] text-xs h-8 px-3"
                      data-testid={`approve-join-${req.membership_id}`}
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {processing[req.membership_id] ? "..." : "Approve"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReject(req.membership_id)}
                      disabled={processing[req.membership_id]}
                      className="border-red-500 text-red-600 hover:bg-red-50 font-['Nunito'] text-xs h-8 px-3"
                      data-testid={`reject-join-${req.membership_id}`}
                    >
                      <XCircle className="w-3 h-3 mr-1" />
                      {processing[req.membership_id] ? "..." : "Reject"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// =============================================================================
// MAIN ADMIN DASHBOARD COMPONENT
// =============================================================================
export default function AdminDashboard({ user }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("reports");

  useEffect(() => {
    if (!user?.is_site_admin) {
      toast.error("Access denied. Site admin privileges required.");
      navigate("/dashboard");
      return;
    }
    loadStats();
  }, [user, navigate]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const res = await getAdminStats();
      setStats(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Access denied");
        navigate("/dashboard");
      }
    }
    setLoading(false);
  };

  if (!user?.is_site_admin) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--liturgical-primary)]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2] pt-20 pb-8" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-[var(--liturgical-primary)] rounded-lg">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-['Cinzel'] font-bold text-[#2C2C2C]">Site Admin Dashboard</h1>
          </div>
          <p className="text-[#6B665E] font-['Nunito']">Manage users, parishes, content, and system settings</p>
        </div>

        {/* Quick Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard title="Pending Reports" value={stats.pending_reports} icon={AlertTriangle} color="bg-red-500" />
            <StatCard title="Total Users" value={stats.total_users} icon={Users} color="bg-blue-500" />
            <StatCard title="Total Posts" value={stats.total_posts} icon={FileText} color="bg-green-500" />
            <StatCard title="Recent Reports" value={stats.recent_reports} icon={Clock} color="bg-yellow-500" subtitle="Last 7 days" />
          </div>
        )}

        {/* Quick Actions */}
        <Card className="mb-6 bg-white/80 border-[#E8E4DC]">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Nunito'] text-[#2C2C2C] flex items-center gap-2">
              <ChevronRight className="w-5 h-5" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button 
              onClick={() => navigate('/admin/import-parishes')}
              className="bg-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Import Parishes
            </Button>
          </CardContent>
        </Card>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-[#E8E4DC] mb-4 flex-wrap h-auto p-1">
            <TabsTrigger value="reports" className="data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white">
              <AlertTriangle className="w-4 h-4 mr-1" /> Reports
            </TabsTrigger>
            <TabsTrigger value="join-requests" className="data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white" data-testid="join-requests-tab-trigger">
              <UserPlus className="w-4 h-4 mr-1" /> Join Requests
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-1" /> Users
            </TabsTrigger>
            <TabsTrigger value="parishes" className="data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white">
              <Building className="w-4 h-4 mr-1" /> Parishes
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white">
              <BarChart3 className="w-4 h-4 mr-1" /> Analytics
            </TabsTrigger>
            <TabsTrigger value="announcements" className="data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white">
              <Megaphone className="w-4 h-4 mr-1" /> Announcements
            </TabsTrigger>
            <TabsTrigger value="content" className="data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-1" /> Content
            </TabsTrigger>
            <TabsTrigger value="invites" className="data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white">
              <Ticket className="w-4 h-4 mr-1" /> Invites
            </TabsTrigger>
            <TabsTrigger value="support" className="data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white">
              <HelpCircle className="w-4 h-4 mr-1" /> Support
            </TabsTrigger>
            <TabsTrigger value="directory" className="data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white">
              <Store className="w-4 h-4 mr-1" /> Directory
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white">
              <Settings className="w-4 h-4 mr-1" /> Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="reports"><ReportsTab onActionComplete={loadStats} /></TabsContent>
          <TabsContent value="join-requests"><JoinRequestsTab /></TabsContent>
          <TabsContent value="users"><UsersTab /></TabsContent>
          <TabsContent value="parishes"><ParishesTab /></TabsContent>
          <TabsContent value="analytics"><AnalyticsTab /></TabsContent>
          <TabsContent value="announcements"><AnnouncementsTab /></TabsContent>
          <TabsContent value="content"><ContentTab /></TabsContent>
          <TabsContent value="invites"><InvitesTab /></TabsContent>
          <TabsContent value="support"><SupportTab /></TabsContent>
          <TabsContent value="directory"><DirectoryAdminTab /></TabsContent>
          <TabsContent value="settings"><SettingsTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
